// Hợp đồng của crm-service (đặc tả docs/backend/services/crm-service.md): schema zod của mọi lệnh/truy vấn
// (gateway truyền nguyên body/query, service kiểm lại), DTO/read model web hiển thị, tên & payload sự kiện.
// Quy ước: tiền VND là số nguyên (number, ≤ 9e15); thời điểm ISO 8601 UTC; ngày YYYY-MM-DD, tháng YYYY-MM theo
// Asia/Ho_Chi_Minh; id là UUID. Sự kiện không bao giờ chứa SĐT đầy đủ, email, địa chỉ, CCCD, nội dung chăm sóc.
import { z } from 'zod';
import { normPhone, validPhone } from '../phone.js';

// ---------- Bộ dựng schema (thông điệp tiếng Việt) ----------
const REQUIRED = 'Thiếu dữ liệu bắt buộc';
const msg = (message: string) => ({ errorMap: () => ({ message }) });
const str = (required = REQUIRED) => z.string({ required_error: required, invalid_type_error: 'Phải là chuỗi ký tự' });
const text = (max = 200) => str().trim().max(max, `Tối đa ${max} ký tự`);
const reqText = (max: number, message: string) => str(message).trim().min(1, message).max(max, `Tối đa ${max} ký tự`);
const uuid = str().uuid('Mã định danh không hợp lệ');
const int = (message = 'Phải là số nguyên') => z.number({ required_error: REQUIRED, invalid_type_error: 'Phải là số' }).int(message);
const version = int().min(0, 'Phiên bản không hợp lệ');
const money = z
  .number({ required_error: 'Nhập số tiền', invalid_type_error: 'Số tiền phải là số' })
  .int('Số tiền phải là số nguyên')
  .min(0, 'Số tiền không được âm')
  .max(9e15, 'Số tiền vượt giới hạn');
const bool = z.boolean({ required_error: REQUIRED, invalid_type_error: 'Phải là đúng/sai' });
const isRealDate = (s: string) => {
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
};
const dateKey = str().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ngày dạng YYYY-MM-DD').refine(isRealDate, 'Ngày không tồn tại');
const isoTimeReq = (required = REQUIRED) => str(required).datetime({ offset: true, message: 'Thời điểm phải theo ISO 8601' });
const isoTime = isoTimeReq();
/** Tham số truy vấn (query string) ⇒ số. */
const qInt = (min: number, max: number, def?: number) => {
  const base = z.coerce.number({ invalid_type_error: 'Phải là số' }).int('Phải là số nguyên').min(min, `Tối thiểu ${min}`).max(max, `Tối đa ${max}`);
  return def === undefined ? base.optional() : base.default(def);
};
const boolish = z.preprocess(
  (v) => (v === 'true' || v === '1' || v === true ? true : v === 'false' || v === '0' || v === false ? false : undefined),
  z.boolean().optional(),
);
const page = qInt(1, 100_000, 1);
const pageSize = (d = 50, max = 500) => qInt(1, max, d);
/** Trường chuỗi tuỳ chọn: chuỗi rỗng ⇒ null. */
const optText = (max = 200) => z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? null : v), text(max).nullish());

// ---------- Hằng số dùng chung FE/BE (nhãn hiển thị ở packages/domain) ----------
export const STAGE_KEYS = ['new', 'contacted', 'qualified', 'viewing', 'negotiating', 'deposited', 'contracted', 'lost'] as const;
export type Stage = (typeof STAGE_KEYS)[number];
/** Giai đoạn đổi thủ công được (Đã cọc / Đã ký HĐ chỉ do sự kiện giao dịch — C6). */
export const MANUAL_STAGES = ['new', 'contacted', 'qualified', 'viewing', 'negotiating', 'lost'] as const;
export const RATINGS = ['hot', 'warm', 'cold'] as const;
export type Rating = (typeof RATINGS)[number];
export const ACTIVITY_TYPES = ['call', 'zalo', 'meet', 'viewing', 'quote', 'note'] as const;
export type ActivityType = (typeof ACTIVITY_TYPES)[number];
export const TASK_TYPES = ['viewing', 'call', 'meet', 'payment', 'doc', 'other'] as const;
export type TaskType = (typeof TASK_TYPES)[number];
export const TASK_TYPE_LABEL: Record<TaskType, string> = {
  viewing: 'Dẫn xem dự án',
  call: 'Gọi / nhắn chăm sóc',
  meet: 'Gặp khách',
  payment: 'Nhắc thanh toán',
  doc: 'Hồ sơ / ký HĐ',
  other: 'Khác',
};
export const LOST_REASONS = ['Không đủ tài chính', 'Mua dự án khác', 'Không liên lạc được', 'Sai thông tin', 'Chưa có nhu cầu', 'Khác'] as const;
export const NEED_PURPOSES = ['Ở', 'Đầu tư', 'Ở + cho thuê', 'Kinh doanh'] as const;
export type NeedPurpose = (typeof NEED_PURPOSES)[number];
export const CUSTOMER_ORIGINS = ['form', 'quick_create', 'import', 'migration'] as const;
export type CustomerOrigin = (typeof CUSTOMER_ORIGINS)[number];
export const OWNER_MODES = ['self', 'staff', 'auto', 'pool'] as const;
export type OwnerMode = (typeof OWNER_MODES)[number];
export const ASSIGN_MODES = ['manual', 'auto', 'claimPool', 'claimUnprotected', 'handover', 'handoverRevert'] as const;
export type AssignMode = (typeof ASSIGN_MODES)[number];
export const STAGE_CAUSES = ['manual', 'activity', 'task', 'dealSubmitted', 'dealConfirmed', 'dealRejected', 'contractSigned', 'dealCancelled'] as const;
export type StageCause = (typeof STAGE_CAUSES)[number];
export const CUSTOMER_FILTERS = ['all', 'mine', 'pool', 'sla', 'expiring', 'hot'] as const;
export type CustomerFilter = (typeof CUSTOMER_FILTERS)[number];
export const TASK_TABS = ['today', 'overdue', 'upcoming', 'done', 'week'] as const;
export type TaskTab = (typeof TASK_TABS)[number];
export const TASK_ORIGINS = ['manual', 'followUp', 'migration'] as const;
export type TaskOrigin = (typeof TASK_ORIGINS)[number];
export const ACTIVITY_ORIGINS = ['manual', 'task', 'system'] as const;
export type ActivityOrigin = (typeof ACTIVITY_ORIGINS)[number];
export const ACTIVITY_SYSTEM_KINDS = ['transfer', 'claim', 'handover', 'handoverRevert'] as const;
export type ActivitySystemKind = (typeof ACTIVITY_SYSTEM_KINDS)[number];
export const REVEAL_CONTEXTS = ['drawer', 'edit_form', 'deal_view', 'booking'] as const;
export type RevealContext = (typeof REVEAL_CONTEXTS)[number];
export const CLAIM_KINDS = ['pool', 'unprotected'] as const;
export const IMPORT_TARGETS = ['auto', 'pool', 'staff'] as const;
export type ImportTarget = (typeof IMPORT_TARGETS)[number];
export const IMPORT_SOURCE_KINDS = ['paste', 'csv'] as const;
export type ImportSourceKind = (typeof IMPORT_SOURCE_KINDS)[number];
export const IMPORT_ROW_STATUSES = ['ok', 'missing_name', 'invalid_phone', 'duplicate_in_file', 'duplicate_existing', 'conflict_at_commit'] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];
export const IMPORT_ROW_STATUS_LABEL: Record<ImportRowStatus, string> = {
  ok: 'Hợp lệ',
  missing_name: 'Thiếu tên',
  invalid_phone: 'SĐT không hợp lệ',
  duplicate_in_file: 'Trùng trong file',
  duplicate_existing: 'Đã có trong hệ thống',
  conflict_at_commit: 'Trùng khi nhập',
};
export const CUSTOMER_OPTION_PURPOSES = ['lock', 'booking', 'task'] as const;
export const ASSIGNEE_PURPOSES = ['manual', 'auto', 'import'] as const;
export const DEAL_LINK_STATUSES = ['pending', 'deposited', 'contracted', 'completed', 'rejected', 'cancelled'] as const;
export const INVALID_PHONE_MESSAGE = 'Số điện thoại Việt Nam không hợp lệ (10 số, đầu 03/05/07/08/09)';

// ---------- Trường dùng lại ----------
/** SĐT chính: chuẩn hoá bằng normPhone (C2) rồi kiểm validPhone ⇒ đầu ra là số đã chuẩn hoá. */
const phoneField = reqText(40, 'Nhập số điện thoại').transform((v) => normPhone(v)).refine(validPhone, INVALID_PHONE_MESSAGE);
/** SĐT phụ: rỗng/null ⇒ null; có giá trị ⇒ chuẩn hoá + kiểm (C3). */
const phone2Field = z.preprocess(
  (v) => (v === undefined ? undefined : v === null || (typeof v === 'string' && v.trim() === '') ? null : v),
  str().max(40, 'Tối đa 40 ký tự').transform((v) => normPhone(v)).refine(validPhone, INVALID_PHONE_MESSAGE).nullable().optional(),
);
const emailField = z.preprocess(
  (v) => (typeof v === 'string' ? (v.trim() === '' ? null : v.trim().toLowerCase()) : v),
  str().email('Email không hợp lệ').max(200, 'Tối đa 200 ký tự').nullish(),
);
/** CCCD/CMND: chỉ chữ số, ≤ 20 (lưu mã hoá; không vào sự kiện). */
const idNoField = z.preprocess(
  (v) => (typeof v === 'string' ? (v.trim() === '' ? null : v.replace(/\s/g, '')) : v),
  str().regex(/^\d{1,20}$/, 'Số CCCD chỉ gồm chữ số (tối đa 20)').nullish(),
);
const tagsField = z.array(reqText(40, 'Nhãn không được rỗng'), { invalid_type_error: 'Nhãn phải là danh sách' }).max(20, 'Tối đa 20 nhãn');

export const needsInput = z
  .object({
    unitType: optText(60),
    budgetMin: money.default(0),
    budgetMax: money.default(0),
    area: z.number({ invalid_type_error: 'Diện tích phải là số' }).positive('Diện tích phải lớn hơn 0').max(1_000_000, 'Diện tích quá lớn').nullish(),
    projectIds: z.array(uuid).max(50, 'Tối đa 50 dự án').default([]),
    purpose: z.enum(NEED_PURPOSES, msg('Mục đích không hợp lệ')).nullish(),
    direction: optText(60),
  })
  .refine((n) => !(n.budgetMin > 0 && n.budgetMax > 0) || n.budgetMin <= n.budgetMax, {
    message: 'Ngân sách tối thiểu không được vượt ngân sách tối đa',
    path: ['budgetMax'],
  });
export const consentInput = z.object({ given: bool, channel: optText(60) });

// ---------- Lệnh: khách hàng ----------
export const customerRef = z.object({ customerId: uuid });

/** Tạo khách (form "Khách mới" / tạo nhanh ở phiếu chốt căn). Không có customers.assign ⇒ service ép ownerId = actor. */
export const createCustomerInput = z
  .object({
    name: reqText(120, 'Nhập họ tên khách'),
    phone: phoneField,
    phone2: phone2Field,
    email: emailField,
    address: optText(300),
    source: optText(100),
    rating: z.enum(RATINGS, msg('Mức độ không hợp lệ')).default('warm'),
    needs: needsInput.optional(),
    consent: consentInput.default({ given: false }),
    idNo: idNoField,
    note: optText(4000),
    tags: tagsField.default([]),
    ownerMode: z.enum(OWNER_MODES, msg('Cách phân khách không hợp lệ')).optional(),
    ownerId: uuid.nullish(),
    teamId: uuid.nullish(),
    origin: z.enum(['form', 'quick_create'], msg('Nguồn tạo không hợp lệ')).default('form'),
  })
  .refine((v) => v.ownerMode !== 'staff' || !!v.ownerId, { message: 'Chọn người phụ trách', path: ['ownerId'] });

/**
 * Sửa hồ sơ (PATCH, If-Match = profileVersion). Trường vắng mặt = giữ nguyên.
 * phone: rỗng = giữ nguyên (người không phải chủ chỉ thấy số che); phone2: rỗng = giữ nguyên, null = xoá.
 */
export const updateCustomerProfileInput = z.object({
  customerId: uuid,
  expectedProfileVersion: version,
  patch: z.object({
    name: reqText(120, 'Nhập họ tên khách').optional(),
    phone: z.preprocess((v) => (typeof v === 'string' && v.trim() === '' ? undefined : v), phoneField.optional()),
    phone2: z.preprocess(
      (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
      str().max(40, 'Tối đa 40 ký tự').transform((v) => normPhone(v)).refine(validPhone, INVALID_PHONE_MESSAGE).nullable().optional(),
    ),
    email: emailField,
    address: optText(300),
    source: reqText(100, 'Chọn nguồn khách').optional(),
    rating: z.enum(RATINGS, msg('Mức độ không hợp lệ')).optional(),
    needs: needsInput.optional(),
    idNo: idNoField,
    note: optText(4000),
    tags: tagsField.optional(),
    consent: consentInput.optional(),
  }),
});

export const changeCustomerStageInput = z
  .object({
    customerId: uuid,
    toStage: z.enum(MANUAL_STAGES, msg('Giai đoạn này không đổi thủ công được')),
    lostReason: z.enum(LOST_REASONS, msg('Chọn lý do thất bại')).optional(),
  })
  .refine((v) => v.toStage !== 'lost' || !!v.lostReason, { message: 'Chọn lý do thất bại', path: ['lostReason'] });

export const logCustomerActivityInput = z.object({
  customerId: uuid,
  type: z.enum(ACTIVITY_TYPES, msg('Loại chăm sóc không hợp lệ')),
  text: reqText(2000, 'Nhập nội dung chăm sóc'),
  /** "Hẹn chăm sóc lại" ⇒ tạo lịch gọi cho chủ khách (T8). */
  followUpAt: isoTime.optional(),
});

export const assignCustomerInput = z
  .object({
    customerId: uuid,
    mode: z.enum(['staff', 'auto'], msg('Cách phân không hợp lệ')),
    toStaffId: uuid.optional(),
    teamId: uuid.optional(),
    expectedVersion: version.optional(),
  })
  .refine((v) => v.mode !== 'staff' || !!v.toStaffId, { message: 'Chọn người nhận', path: ['toStaffId'] });
export const returnCustomerToPoolInput = customerRef;
export const autoAssignPoolInput = z.object({
  customerIds: z.array(uuid).min(1, 'Chọn ít nhất một khách').max(500, 'Tối đa 500 khách mỗi lần').optional(),
  teamId: uuid.optional(),
});
/** Gateway POST /crm/customers/:id/claim { kind } ⇒ ClaimPoolCustomer (pool) hoặc ClaimUnprotectedCustomer (unprotected). */
export const claimCustomerBody = z.object({ kind: z.enum(CLAIM_KINDS, msg('Kiểu nhận khách không hợp lệ')) });
export const claimPoolCustomerInput = customerRef;
export const claimUnprotectedCustomerInput = customerRef;
export const revealCustomerPhoneInput = z.object({ customerId: uuid, context: z.enum(REVEAL_CONTEXTS, msg('Ngữ cảnh không hợp lệ')).default('drawer') });

export const requestCustomerExportInput = z.object({ format: z.enum(['csv'], msg('Định dạng không hỗ trợ')).default('csv') });
export const customerExportRef = z.object({ exportId: uuid });
export const issueCustomerExportDownloadInput = customerExportRef;

/** Nhập từ Excel: FE dán TSV hoặc đọc tệp CSV ở trình duyệt rồi gửi nội dung văn bản (≤ 5 MB, ≤ 5.000 dòng — I1). */
export const uploadCustomerImportInput = z.object({
  text: str().min(1, 'Dán dữ liệu từ Excel hoặc chọn tệp CSV').max(5_000_000, 'Tệp tối đa 5 MB'),
  sourceKind: z.enum(IMPORT_SOURCE_KINDS, msg('Kiểu nguồn không hợp lệ')).default('paste'),
  fileName: optText(200),
});
export const customerImportRef = z.object({ batchId: uuid });
export const commitCustomerImportInput = z
  .object({
    batchId: uuid,
    target: z.enum(IMPORT_TARGETS, msg('Chọn cách phân khách')),
    toStaffId: uuid.optional(),
    defaultSource: reqText(100, 'Chọn nguồn mặc định'),
    consentGiven: bool.default(false),
  })
  .refine((v) => v.target !== 'staff' || !!v.toStaffId, { message: 'Chọn người nhận', path: ['toStaffId'] });
export const discardCustomerImportInput = customerImportRef;

// ---------- Lệnh: lịch hẹn & công việc ----------
export const taskRef = z.object({ taskId: uuid });
export const createTaskInput = z.object({
  type: z.enum(TASK_TYPES, msg('Loại lịch hẹn không hợp lệ')),
  /** Trống ⇒ defaultTaskTitle(type, tên khách). */
  title: optText(200),
  customerId: uuid.nullish(),
  projectId: uuid.nullish(),
  /** Trống ⇒ actor (không có tasks.assign ⇒ luôn actor — T3). */
  staffId: uuid.optional(),
  dueAt: isoTimeReq('Chọn thời gian hẹn'),
  note: optText(2000),
});
export const updateTaskInput = z.object({
  taskId: uuid,
  expectedVersion: version,
  patch: z.object({
    type: z.enum(TASK_TYPES, msg('Loại lịch hẹn không hợp lệ')).optional(),
    title: optText(200),
    customerId: uuid.nullable().optional(),
    projectId: uuid.nullable().optional(),
    staffId: uuid.optional(),
    dueAt: isoTime.optional(),
    note: optText(2000),
  }),
});
export const completeTaskInput = z.object({ taskId: uuid, result: reqText(2000, 'Nhập kết quả') });
export const deleteTaskInput = taskRef;

// ---------- Truy vấn công khai ----------
const customerListFilters = {
  stage: z.enum(STAGE_KEYS, msg('Giai đoạn không hợp lệ')).optional(),
  source: text(100).optional(),
  ownerId: uuid.optional(),
  /** Tên không dấu; chữ số SĐT (≥ 3) chỉ khớp khách được xem số. */
  q: text(200).optional(),
};
export const listCustomersInput = z.object({
  filter: z.enum(CUSTOMER_FILTERS, msg('Bộ lọc không hợp lệ')).default('all'),
  ...customerListFilters,
  sort: z.enum(['lastActivityAt', '-lastActivityAt', 'name', '-name', 'stage', '-stage'], msg('Cách sắp xếp không hợp lệ')).default('-lastActivityAt'),
  page,
  pageSize: pageSize(50, 500),
});
export const getCustomerFacetsInput = z.object(customerListFilters);
export const getCustomerInput = customerRef;
export const listCustomerActivitiesInput = z.object({
  customerId: uuid,
  cursor: text(200).optional(),
  limit: qInt(1, 200, 40),
  /** Đọc tiếp phần lưu trữ (ngoài 200 mục mới nhất — A5). */
  includeArchived: boolish,
});
export const listCustomerTasksInput = customerRef;
export const getCustomerUnitMatchesInput = z.object({
  customerId: uuid,
  limit: qInt(1, 40, 6),
  minScore: qInt(0, 100, 0),
  projectId: uuid.optional(),
});
/** Kiểm trùng khi gõ (không lọc phạm vi — C4); chỉ tra khi số chuẩn hoá ≥ 10 chữ số. */
export const checkPhoneDuplicateInput = z.object({ phone: str().max(40, 'Tối đa 40 ký tự'), exceptId: uuid.optional() });
export const searchCustomersInput = z.object({ q: reqText(200, 'Nhập từ khoá'), limit: qInt(1, 20, 6) });
export const listCustomerOptionsInput = z.object({
  purpose: z.enum(CUSTOMER_OPTION_PURPOSES, msg('Mục đích không hợp lệ')),
  q: text(200).optional(),
  limit: qInt(1, 200, 50),
});
export const listAssignableStaffInput = z.object({
  purpose: z.enum(ASSIGNEE_PURPOSES, msg('Mục đích không hợp lệ')).default('manual'),
  excludeStaffId: uuid.optional(),
  teamId: uuid.optional(),
});
export const getCustomerAlertSummaryInput = z.object({});
export const getCustomerImportInput = customerImportRef;
export const getCustomerExportInput = customerExportRef;
export const listUnitTypesInput = z.object({});
export const listTasksInput = z.object({
  tab: z.enum(TASK_TABS, msg('Tab không hợp lệ')).default('today'),
  /** Trống = "Cả đội" (mọi người trong phạm vi tasks.view). */
  staffId: uuid.optional(),
  /** Ngày bất kỳ trong tuần cần xem (tab week). */
  weekOf: dateKey.optional(),
  page,
  pageSize: pageSize(100, 500),
});
export const getTaskCountsInput = z.object({ staffId: uuid.optional() });
export const getTaskInput = taskRef;

// ---------- Truy vấn nội bộ (service ↔ service, không qua gateway) ----------
export const getCustomerCountsByOwnerInput = z.object({ ownerIds: z.array(uuid).max(1000, 'Tối đa 1.000 nhân sự') });
export const countOwnedCustomersInput = z.object({ staffId: uuid });
export const countOpenTasksInput = z.object({ staffId: uuid });
/** booking (giữ căn / gắn khách) và deal (gửi phiếu cọc) hỏi đồng bộ với ngữ cảnh người thao tác. */
export const checkCustomerUsableInput = z.object({ customerId: uuid, purpose: z.enum(['lock', 'booking'], msg('Mục đích không hợp lệ')) });
export const getCustomerSummariesInput = z.object({ customerIds: z.array(uuid).max(1000, 'Tối đa 1.000 khách') });
/**
 * gateway GET /booking/matches?customerId= — nhu cầu khách (kiểm khả kiến theo customers.view). Kết quả: CustomerNeeds
 * (truyền thẳng làm `needs` của booking.matchUnitsForNeedsInput; khoá thừa `purpose` bị zod bỏ).
 */
export const getCustomerNeedsInput = customerRef;
export const getCustomerSnapshotPageInput = z.object({ afterId: uuid.optional(), limit: qInt(1, 1000, 500), updatedSince: isoTime.optional() });
/** performance đối soát viewing_activity / has_viewing: hoạt động "Dẫn xem" (gồm phần lưu trữ) theo id tăng dần; updatedSince lọc theo `at`. */
export const getViewingActivityPageInput = z.object({ afterId: uuid.optional(), updatedSince: isoTime.optional(), limit: qInt(1, 1000, 500) });
/** notification đối soát nhắc lịch (W6): lịch chưa xong theo id tăng dần. */
export const getOpenTaskSnapshotPageInput = z.object({ afterId: uuid.optional(), limit: qInt(1, 1000, 500) });
export const listActivityStatsInput = z.object({
  staffIds: z.array(uuid).max(1000, 'Tối đa 1.000 nhân sự'),
  from: isoTime,
  to: isoTime,
  type: z.enum(ACTIVITY_TYPES, msg('Loại chăm sóc không hợp lệ')).optional(),
});
/** Quản trị nội bộ (Q7): bật/tắt nhận lead chia tự động của một nhân sự (customers.assign phủ người đó). */
export const setLeadReceivingInput = z.object({ staffId: uuid, receivesLeads: bool });
/**
 * Tải tệp xuất qua URL ký hạn (IssueCustomerExportDownload trả `url` chứa `token`). Gateway route công khai
 * GET /crm/customer-exports/:id/file?token= — token mang tenant + người yêu cầu + hạn 15 phút (X3).
 */
export const getCustomerExportFileInput = z.object({ exportId: uuid, token: str().min(1, 'Thiếu mã tải').max(2000, 'Mã tải không hợp lệ') });

/** Tên RPC: crm.cmd.<Name> / crm.qry.<Name>. */
export const RPC = {
  cmd: [
    'CreateCustomer',
    'UpdateCustomerProfile',
    'ChangeCustomerStage',
    'LogCustomerActivity',
    'AssignCustomer',
    'ReturnCustomerToPool',
    'AutoAssignPool',
    'ClaimPoolCustomer',
    'ClaimUnprotectedCustomer',
    'RevealCustomerPhone',
    'RequestCustomerExport',
    'IssueCustomerExportDownload',
    'UploadCustomerImport',
    'CommitCustomerImport',
    'DiscardCustomerImport',
    'CreateTask',
    'UpdateTask',
    'CompleteTask',
    'DeleteTask',
    'SetLeadReceiving',
  ],
  qry: [
    'ListCustomers',
    'GetCustomerFacets',
    'GetCustomer',
    'ListCustomerActivities',
    'ListCustomerTasks',
    'GetCustomerUnitMatches',
    'CheckPhoneDuplicate',
    'SearchCustomers',
    'ListCustomerOptions',
    'ListAssignableStaff',
    'GetCustomerAlertSummary',
    'GetCustomerImport',
    'GetCustomerExport',
    'ListUnitTypes',
    'ListTasks',
    'GetTaskCounts',
    'GetTask',
    // nội bộ
    'GetCustomerCountsByOwner',
    'CountOwnedCustomers',
    'CountOpenTasks',
    'CheckCustomerUsable',
    'GetCustomerSummaries',
    'GetCustomerNeeds',
    'GetCustomerSnapshotPage',
    'ListActivityStats',
    'GetCustomerExportFile',
    'GetViewingActivityPage',
    'GetOpenTaskSnapshotPage',
  ],
} as const;

// ---------- DTO / read model ----------
export interface CustomerNeeds {
  unitType: string | null;
  budgetMin: number;
  budgetMax: number;
  area: number | null;
  projectIds: string[];
  purpose: NeedPurpose | null;
  direction: string | null;
}
export interface CustomerConsent {
  given: boolean;
  at: string | null;
  channel: string | null;
}
/** Trạng thái SLA liên hệ đầu (slaState); null khi khách ở kho chung. */
export interface SlaFlag {
  dueAt: string;
  met: boolean;
  ok: boolean;
  overdue: boolean;
}
export interface ProtectionFlag {
  permanent: boolean;
  until: string | null;
  expired: boolean;
  /** Còn < 3 ngày ⇒ số ngày còn lại (ceil); ngược lại null. */
  expiringDays: number | null;
}
export interface CustomerRow {
  id: string;
  name: string;
  /** Số đầy đủ khi người xem là chủ khách, ngược lại maskPhone. */
  phoneDisplay: string;
  /** Được bấm "Hiện số" (canSeePhone ∧ không phải chủ). */
  phoneRevealable: boolean;
  rating: Rating;
  stage: Stage;
  lostReason: string | null;
  source: string;
  ownerId: string | null;
  ownerName: string | null;
  needUnitType: string | null;
  needBudgetMin: number;
  needBudgetMax: number;
  lastActivityAt: string;
  assignedAt: string | null;
  firstContactAt: string | null;
  sla: SlaFlag | null;
  protection: ProtectionFlag;
  consentGiven: boolean;
  hot: boolean;
  isMine: boolean;
  inPool: boolean;
  createdAt: string;
}
export interface CustomerList {
  items: CustomerRow[];
  total: number;
  page: number;
  pageSize: number;
  serverTime: string;
}
export interface CustomerFacets {
  chips: { all: number; mine: number; pool?: number; sla: number; expiring: number; hot: number };
  stageCounts: Partial<Record<Stage, number>>;
  owners: { staffId: string; name: string }[];
}
export interface CustomerPermissions {
  canEdit: boolean;
  canAssign: boolean;
  canReturnToPool: boolean;
  canClaimPool: boolean;
  canRevealPhone: boolean;
  canLogActivity: boolean;
  canChangeStage: boolean;
  canLockForCustomer: boolean;
}
export interface CustomerDetail {
  id: string;
  name: string;
  /** Chỉ có khi người xem là chủ khách. */
  phone: string | null;
  phone2: string | null;
  phoneDisplay: string;
  phone2Display: string | null;
  email: string | null;
  address: string | null;
  source: string;
  rating: Rating;
  stage: Stage;
  lostReason: string | null;
  needs: CustomerNeeds;
  /** Tên dự án quan tâm (bỏ dự án đã xoá). */
  projects: { projectId: string; name: string }[];
  consent: CustomerConsent;
  /** Chỉ trả cho người sửa được khách (C15). */
  idNo: string | null;
  note: string | null;
  tags: string[];
  ownerId: string | null;
  ownerName: string | null;
  assignedAt: string | null;
  firstContactAt: string | null;
  slaDueAt: string | null;
  lastActivityAt: string;
  sla: SlaFlag | null;
  protection: ProtectionFlag;
  hot: boolean;
  isMine: boolean;
  inPool: boolean;
  origin: CustomerOrigin;
  openTaskCount: number;
  dealCount: number;
  createdAt: string;
  createdBy: string | null;
  profileVersion: number;
  version: number;
  permissions: CustomerPermissions;
}
export interface CreateCustomerResult {
  customerId: string;
  ownerId: string | null;
  ownerName: string | null;
  profileVersion: number;
}
export interface CustomerActivityView {
  id: string;
  type: ActivityType;
  at: string;
  byStaffId: string;
  byName: string | null;
  text: string;
  origin: ActivityOrigin;
  systemKind: ActivitySystemKind | null;
  taskId: string | null;
  archived: boolean;
}
export interface CustomerActivityPage {
  items: CustomerActivityView[];
  nextCursor: string | null;
}
export interface UnitMatchView {
  unitId: string;
  code: string;
  projectId: string;
  projectName: string;
  type: string;
  area: number | null;
  direction: string | null;
  price: number;
  score: number;
  why: string[];
}
export interface PhoneDuplicateResult {
  normalized: string;
  valid: boolean;
  duplicate: null | {
    customerId: string;
    name: string;
    phoneMasked: string;
    ownerId: string | null;
    ownerName: string | null;
    inPool: boolean;
    isMine: boolean;
    protectedUntil: string | null;
    protectionPermanent: boolean;
    protected: boolean;
    canOpen: boolean;
    canClaim: boolean;
  };
}
export interface CustomerSearchHit {
  id: string;
  name: string;
  phoneDisplay: string;
  stage: Stage;
}
export interface CustomerOption extends CustomerSearchHit {
  ownerId: string | null;
}
export interface AssignableStaff {
  staffId: string;
  name: string;
  roleLabel: string | null;
  teamId: string | null;
  onLeaveToday: boolean;
  receivesLeads: boolean;
  loadToday: number;
  lastAssignedAt: string | null;
}
export interface CustomerAlertSummary {
  slaOverdue: number;
  /** Chỉ khi người gọi có customers.assign. */
  pool?: number;
  expiringMine: number;
  overdueTasksMine: number;
  createdLast7Days: number;
  /**
   * Tenant đã có ít nhất một khách (cờ thiết lập toàn tenant, không theo phạm vi người gọi, không có dữ liệu khách) — "Hướng dẫn
   * bắt đầu" / mục "khách đầu tiên". Tuỳ chọn trong kiểu để tương thích ngược; crm luôn điền.
   */
  hasAnyCustomers?: boolean;
}
export interface AutoAssignResult {
  assigned: number;
  remaining: number;
}
export interface RevealPhoneResult {
  phone: string;
  phone2: string | null;
}
export interface ImportRowView {
  rowNo: number;
  name: string;
  phone: string;
  email: string | null;
  source: string | null;
  note: string | null;
  budget: number;
  status: ImportRowStatus;
  customerId: string | null;
}
export interface CustomerImportResult {
  created: number;
  skipped: { missingName: number; invalidPhone: number; duplicateInFile: number; duplicateExisting: number; conflictAtCommit: number; noAssigneeFallbackToPool: number };
}
export interface CustomerImportView {
  batchId: string;
  status: 'validated' | 'committing' | 'completed' | 'discarded' | 'expired';
  sourceKind: ImportSourceKind;
  /** Chỉ số cột nhận diện (I2), -1 = không có. */
  headerMap: { name: number; phone: number; email: number; source: number; note: number; budget: number };
  totalRows: number;
  validRows: number;
  counts: Record<ImportRowStatus, number>;
  /** 200 dòng đầu để xem trước. */
  preview: ImportRowView[];
  options: { target: ImportTarget; toStaffId: string | null; defaultSource: string; consentGiven: boolean } | null;
  result: CustomerImportResult | null;
  createdAt: string;
  expiresAt: string;
}
export interface CustomerExportView {
  exportId: string;
  status: 'requested' | 'building' | 'completed' | 'failed' | 'expired';
  rowCount: number | null;
  fileName: string | null;
  expiresAt: string | null;
  error: string | null;
}
export interface ExportDownload {
  url: string;
  expiresAt: string;
}
export interface TaskRow {
  id: string;
  type: TaskType;
  title: string;
  customerId: string | null;
  customerName: string | null;
  projectId: string | null;
  projectName: string | null;
  staffId: string;
  staffName: string | null;
  dueAt: string;
  done: boolean;
  doneAt: string | null;
  result: string | null;
  /** Chưa xong ∧ dueAt < now. */
  late: boolean;
}
export interface TaskList {
  items: TaskRow[];
  total: number;
  page: number;
  pageSize: number;
  serverTime: string;
}
export interface TaskCounts {
  today: number;
  overdue: number;
  upcoming: number;
}
export interface TaskView extends TaskRow {
  note: string | null;
  origin: TaskOrigin;
  completedBy: string | null;
  activityId: string | null;
  createdAt: string;
  createdBy: string;
  createdByName: string | null;
  version: number;
}
// Nội bộ
export interface CustomerUsability {
  usable: boolean;
  /** Mã lý do khi không dùng được (VD CUSTOMER_NOT_OWNED, CUSTOMER_LOST, CUSTOMER_CONTRACTED, NOT_FOUND). */
  reason?: string;
  message?: string;
  name: string;
  phoneMasked: string;
  ownerId: string | null;
  stage: Stage;
}
export interface CustomerSummary {
  customerId: string;
  name: string;
  phoneMasked: string;
  ownerId: string | null;
  source: string;
  stage: Stage;
  /** Phiên bản aggregate khách (= aggregateVersion của crm.customer.* mới nhất) — consumer đối soát chỉ áp khi mới hơn. crm luôn điền. */
  version?: number;
}
export interface CustomerSnapshot extends CustomerState {
  customerId: string;
  createdAt: string;
  version: number;
  /** Tên khách (booking dựng lại customer_ref còn thiếu). crm luôn điền. */
  name?: string;
  /** Đã có ít nhất một hoạt động "Dẫn xem" (gồm phần lưu trữ) — performance has_viewing. crm luôn điền. */
  hasViewing?: boolean;
}
export interface ActivityStat {
  byStaffId: string;
  type: ActivityType;
  count: number;
}
/** GetCustomerSnapshotPage: trang ảnh chụp trạng thái khách (performance dựng lại phễu). */
export interface CustomerSnapshotPage {
  items: CustomerSnapshot[];
  /** Truyền vào afterId để đọc trang kế; null = hết. */
  nextAfterId: string | null;
}
/** GetViewingActivityPage: một hoạt động "Dẫn xem" (crm.customer.activityLogged type viewing). */
export interface ViewingActivityItem {
  activityId: string;
  customerId: string;
  byStaffId: string;
  at: string;
  /** Dành cho hoạt động bị xoá — crm hiện không xoá hoạt động (A1 chỉ thêm; lưu trữ không phải xoá) nên không bao giờ có. */
  deleted?: boolean;
}
export interface ViewingActivityPage {
  items: ViewingActivityItem[];
  /** Truyền vào afterId để đọc trang kế; null = hết. */
  nextAfterId: string | null;
}
/** GetOpenTaskSnapshotPage: lịch chưa xong (lịch đã xoá không còn bản ghi; lịch đã xong không có trong trang). */
export interface OpenTaskSnapshotItem {
  taskId: string;
  customerId: string | null;
  staffId: string;
  dueAt: string;
  done: false;
  version: number;
}
export interface OpenTaskSnapshotPage {
  items: OpenTaskSnapshotItem[];
  nextAfterId: string | null;
}
/** GetCustomerExportFile: nội dung CSV (UTF-8 có BOM) để gateway trả về dạng tệp tải xuống. */
export interface CustomerExportFile {
  fileName: string;
  contentType: string;
  content: string;
}
/** IssueCustomerExportDownload trả thêm tên tệp (ngoài url + expiresAt). */
export interface ExportDownloadResult extends ExportDownload {
  fileName: string;
}
/** SetLeadReceiving. */
export interface LeadReceivingResult {
  staffId: string;
  receivesLeads: boolean;
  changed: boolean;
}

// ---------- Sự kiện ----------
export const EVENTS = {
  customerCreated: 'crm.customer.created',
  customerProfileUpdated: 'crm.customer.profileUpdated',
  customerConsentChanged: 'crm.customer.consentChanged',
  customerAssigned: 'crm.customer.assigned',
  customerReturnedToPool: 'crm.customer.returnedToPool',
  customerFirstContacted: 'crm.customer.firstContacted',
  customerSlaBreached: 'crm.customer.slaBreached',
  customerStageChanged: 'crm.customer.stageChanged',
  customerActivityLogged: 'crm.customer.activityLogged',
  customerProtectionLapsed: 'crm.customer.protectionLapsed',
  customerPhoneRevealed: 'crm.customer.phoneRevealed',
  customerImportCompleted: 'crm.customerImport.completed',
  customerExportRequested: 'crm.customerExport.requested',
  customerExportCompleted: 'crm.customerExport.completed',
  customerExportFailed: 'crm.customerExport.failed',
  customerExportDownloaded: 'crm.customerExport.downloaded',
  // Phản hồi bước S2 của saga nghỉ việc (hr-service điều phối)
  handoverCompleted: 'crm.handover.completed',
  handoverRejected: 'crm.handover.rejected',
  handoverReverted: 'crm.handover.reverted',
  taskCreated: 'crm.task.created',
  taskUpdated: 'crm.task.updated',
  taskReassigned: 'crm.task.reassigned',
  taskCompleted: 'crm.task.completed',
  taskDeleted: 'crm.task.deleted',
  assigneeLeadReceivingChanged: 'crm.assignee.leadReceivingChanged',
} as const;
export type EventType = (typeof EVENTS)[keyof typeof EVENTS];

/** Khối trạng thái khách đi kèm mọi sự kiện crm.customer.* có đổi trạng thái (consumer chỉ áp aggregateVersion mới hơn). */
export interface CustomerState {
  ownerId: string | null;
  stage: Stage;
  lostReason: string | null;
  rating: Rating;
  source: string;
  assignedAt: string | null;
  firstContactAt: string | null;
  slaDueAt: string | null;
  slaBreachedAt: string | null;
  assignmentSeq: number;
  lastActivityAt: string;
  protectedUntil: string | null;
  protectionPermanent: boolean;
}
type ProfileChangeSet = Partial<{
  name: string;
  phoneMasked: string;
  phone2Masked: string | null;
  source: string;
  rating: Rating;
  needs: CustomerNeeds;
  tags: string[];
}>;

export interface CustomerCreated {
  customerId: string;
  name: string;
  phoneMasked: string;
  phone2Masked: string | null;
  needs: CustomerNeeds;
  consent: CustomerConsent;
  origin: CustomerOrigin;
  importBatchId: string | null;
  createdBy: string | null;
  ownerMode: OwnerMode;
  state: CustomerState;
}
export interface CustomerProfileUpdated {
  customerId: string;
  changes: ProfileChangeSet;
  previous: ProfileChangeSet;
  emailChanged: boolean;
  addressChanged: boolean;
  idNoChanged: boolean;
  noteChanged: boolean;
  cause: 'manual' | 'dealSubmitted';
  state: CustomerState;
}
export interface CustomerConsentChanged {
  customerId: string;
  given: boolean;
  at: string | null;
  channel: string | null;
  previousGiven: boolean;
  origin: 'form' | 'import';
}
export interface CustomerAssigned {
  customerId: string;
  fromOwnerId: string | null;
  toOwnerId: string;
  mode: AssignMode;
  offboardingId: string | null;
  /** Bàn giao nghỉ việc giữ firstContactAt (C9). */
  firstContactKept: boolean;
  state: CustomerState;
}
export interface CustomerReturnedToPool {
  customerId: string;
  fromOwnerId: string;
  state: CustomerState;
}
export interface CustomerFirstContacted {
  customerId: string;
  ownerId: string;
  assignedAt: string;
  firstContactAt: string;
  slaDueAt: string;
  slaMet: boolean;
  via: 'activity' | 'task';
  activityId: string;
  state: CustomerState;
}
export interface CustomerSlaBreached {
  customerId: string;
  ownerId: string;
  assignedAt: string;
  slaDueAt: string;
  breachedAt: string;
  assignmentSeq: number;
}
export interface CustomerStageChanged {
  customerId: string;
  from: Stage;
  to: Stage;
  lostReason: string | null;
  cause: StageCause;
  dealId: string | null;
  state: CustomerState;
}
export interface CustomerActivityLogged {
  customerId: string;
  activityId: string;
  type: ActivityType;
  at: string;
  byStaffId: string;
  ownerId: string | null;
  origin: ActivityOrigin;
  systemKind: ActivitySystemKind | null;
  taskId: string | null;
  state: CustomerState;
}
export interface CustomerProtectionLapsed {
  customerId: string;
  ownerId: string;
  protectedUntil: string;
}
export interface CustomerPhoneRevealed {
  customerId: string;
  ownerId: string | null;
  revealedBy: string;
  revealedAt: string;
  fields: ('phone' | 'phone2')[];
  context: RevealContext;
}
export interface CustomerImportCompleted {
  batchId: string;
  createdBy: string;
  sourceKind: ImportSourceKind;
  totalRows: number;
  created: number;
  skipped: CustomerImportResult['skipped'];
  target: ImportTarget;
  toStaffId: string | null;
  defaultSource: string;
  consentGiven: boolean;
}
export interface CustomerExportRequested {
  exportId: string;
  requestedBy: string;
  format: 'csv';
}
export interface CustomerExportCompleted {
  exportId: string;
  requestedBy: string;
  rowCount: number;
  fileName: string;
  expiresAt: string;
}
export interface CustomerExportFailed {
  exportId: string;
  requestedBy: string;
  error: string;
}
export interface CustomerExportDownloaded {
  exportId: string;
  downloadedBy: string;
  at: string;
}

/** Đã chuyển mọi khách + lịch hẹn/công việc đang mở của người nghỉ cho người nhận. */
export interface HandoverCompleted {
  offboardingId: string;
  idempotencyKey: string; // '<offboardingId>:handover'
  fromStaffId: string;
  toStaffId: string;
  customerIds: string[];
  taskIds: string[];
  customerCount: number;
  taskCount: number;
  completedAt: string;
}
/** Không bàn giao (không đổi dữ liệu): người nhận không hợp lệ lúc xử lý, hoặc đã có yêu cầu hoàn tác trước đó. */
export interface HandoverRejected {
  offboardingId: string;
  idempotencyKey: string;
  reason: 'SUCCESSOR_INELIGIBLE' | 'REVERTED' | (string & {});
  detail?: string | null;
}
/** Đã hoàn tác lần bàn giao (bù trừ C2): mục đã đổi sau bàn giao bị bỏ qua. */
export interface HandoverReverted {
  offboardingId: string;
  idempotencyKey: string; // '<offboardingId>:handover-revert'
  revertedCustomerIds: string[];
  skippedCustomerIds: string[];
  revertedTaskIds: string[];
  skippedTaskIds: string[];
}

type TaskChangeSet = Partial<{ type: TaskType; title: string; customerId: string | null; projectId: string | null; dueAt: string }>;
export interface TaskCreated {
  taskId: string;
  type: TaskType;
  title: string;
  customerId: string | null;
  projectId: string | null;
  staffId: string;
  dueAt: string;
  createdBy: string;
  origin: TaskOrigin;
}
export interface TaskUpdated {
  taskId: string;
  changes: TaskChangeSet;
  previous: TaskChangeSet;
  staffId: string;
  dueAt: string;
}
export interface TaskReassigned {
  taskId: string;
  fromStaffId: string;
  toStaffId: string;
  reason: 'manual' | 'handover' | 'handoverRevert';
  offboardingId: string | null;
  dueAt: string;
  done: boolean;
}
export interface TaskCompleted {
  taskId: string;
  staffId: string;
  customerId: string | null;
  type: TaskType;
  doneAt: string;
  completedBy: string;
  activityId: string | null;
}
export interface TaskDeleted {
  taskId: string;
  staffId: string;
  customerId: string | null;
  wasDone: boolean;
}
export interface AssigneeLeadReceivingChanged {
  staffId: string;
  receivesLeads: boolean;
}

/** Bản đồ loại sự kiện ⇒ payload (consumer: IntegrationEvent<crm.EventPayloads['crm.customer.created']>). */
export interface EventPayloads {
  [EVENTS.customerCreated]: CustomerCreated;
  [EVENTS.customerProfileUpdated]: CustomerProfileUpdated;
  [EVENTS.customerConsentChanged]: CustomerConsentChanged;
  [EVENTS.customerAssigned]: CustomerAssigned;
  [EVENTS.customerReturnedToPool]: CustomerReturnedToPool;
  [EVENTS.customerFirstContacted]: CustomerFirstContacted;
  [EVENTS.customerSlaBreached]: CustomerSlaBreached;
  [EVENTS.customerStageChanged]: CustomerStageChanged;
  [EVENTS.customerActivityLogged]: CustomerActivityLogged;
  [EVENTS.customerProtectionLapsed]: CustomerProtectionLapsed;
  [EVENTS.customerPhoneRevealed]: CustomerPhoneRevealed;
  [EVENTS.customerImportCompleted]: CustomerImportCompleted;
  [EVENTS.customerExportRequested]: CustomerExportRequested;
  [EVENTS.customerExportCompleted]: CustomerExportCompleted;
  [EVENTS.customerExportFailed]: CustomerExportFailed;
  [EVENTS.customerExportDownloaded]: CustomerExportDownloaded;
  [EVENTS.handoverCompleted]: HandoverCompleted;
  [EVENTS.handoverRejected]: HandoverRejected;
  [EVENTS.handoverReverted]: HandoverReverted;
  [EVENTS.taskCreated]: TaskCreated;
  [EVENTS.taskUpdated]: TaskUpdated;
  [EVENTS.taskReassigned]: TaskReassigned;
  [EVENTS.taskCompleted]: TaskCompleted;
  [EVENTS.taskDeleted]: TaskDeleted;
  [EVENTS.assigneeLeadReceivingChanged]: AssigneeLeadReceivingChanged;
}
type AssertNever<T extends never> = T;
/** Lỗi biên dịch nếu có sự kiện trong EVENTS chưa khai báo payload. */
export type _AllEventsHavePayload = AssertNever<Exclude<EventType, keyof EventPayloads>>;
